import { Box, Button, Center, Flex, HStack, Text, VStack } from '@chakra-ui/react';
import { ChatBubble, Check, Eye, Timer, WarningTriangle } from 'iconoir-react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CollabType, UGC_TRACKING_PATH, collabDashboardHref } from '@fluenceur/types';
import { ClipboardRoot, SafeClipboardTrigger } from '@/components/ui/clipboard';
import { ClipboardCopyButton } from '@/components/ui/clipboard/clipboard-button';
import { CopyLinkField } from '@/components/ui/clipboard/CopyLinkField';
import { useAuth } from '@/contexts/auth-context';
import { useInvitationData } from '@/contexts/invitation-context';
import { useContactSummaryQuery } from '@/hooks/queries/use-contact-summary-query';
import {
  DELIVERY_STATUS_META,
  deliveryFailureCauseKey,
  isDeliveredStatus,
  type DeliveryStatusTone,
} from '@/pages/collabs/suivi/collab-delivery-display';
import type { CollabSendResultLine } from '@/stores/invitation-store';
import { refusalMessageKey } from './collab-send-refusals';
import { collabLinkUrl, recipientNameFor } from './collab-wizard-helpers';

interface CollabDoneStepProps {
  onClose: () => void;
}

/**
 * Localized failure text. A line the wizard wrote itself carries its own
 * translated message; a refusal from the server is told by its code, never by
 * the English text the server wrote.
 */
const failureMessage = (line: CollabSendResultLine, t: TFunction): string =>
  line.code ? t(refusalMessageKey(line.code)) : (line.message ?? t(refusalMessageKey(null)));

/** Badge colour per delivery tone — semantic tokens only, matching the F3
 * delivery-failure banner (red.fg). */
const TONE_COLOR: Record<DeliveryStatusTone, string> = {
  ok: 'green.700',
  bad: 'red.fg',
  neutral: 'fg.muted',
};

/**
 * The per-line delivery outcome badge: how the proposal reached
 * (or failed to reach) the recipient. Renders nothing until the backend
 * surfaces `deliveryStatus` on the send result, so the done-step degrades
 * gracefully before that wire lands.
 */
const DeliveryStatusBadge = ({ line }: { line: CollabSendResultLine }) => {
  const { t } = useTranslation();
  if (!line.deliveryStatus) return null;
  const meta = DELIVERY_STATUS_META[line.deliveryStatus];
  const Icon = meta.icon;
  const label =
    line.deliveryStatus === 'failed'
      ? t(deliveryFailureCauseKey(line.deliveryErrorCode ?? null))
      : t(meta.labelKey);
  return (
    <HStack gap="1" color={TONE_COLOR[meta.tone]} mt="0.5">
      <Icon width={11} height={11} />
      <Text fontSize="11px" lineClamp={1}>
        {label}
      </Text>
    </HStack>
  );
};

/**
 * The PRIMARY "reached the recipient" line for a bulk row whose channel
 * delivered (email / affiliate notification / DM) — the dominant info on a
 * delivered line, in the delivery green, icon 14 from the shared status
 * vocabulary. The row already leads with the recipient's name, so the short
 * label is enough. Long values truncate to one line.
 */
const DeliveredStatusLine = ({ line }: { line: CollabSendResultLine }) => {
  const { t } = useTranslation();
  const status = line.deliveryStatus;
  if (!isDeliveredStatus(status)) return null;
  const Icon = DELIVERY_STATUS_META[status].icon;
  return (
    <HStack gap="1.5" color="green.700" minW="0" maxW="full" justify="flex-start">
      <Box display="inline-flex" flexShrink={0}>
        <Icon width={14} height={14} />
      </Box>
      <Text fontSize="12.5px" fontWeight="medium" lineClamp={1} minW="0">
        {t(DELIVERY_STATUS_META[status].labelKey)}
      </Text>
    </HStack>
  );
};

/**
 * The discrete "or copy the link" fallback for a delivered line: the proposal
 * already reached the recipient, so the link is a quiet secondary-link
 * affordance (small, muted, underline on hover) — never a button competing
 * with the status. Localized label, so it copies the same personal link.
 */
const SecondaryCopyLink = ({ lookupKey }: { lookupKey: string }) => {
  const { t } = useTranslation();
  return (
    <ClipboardRoot value={collabLinkUrl(lookupKey)}>
      <SafeClipboardTrigger>
        <Button
          variant="plain"
          size="xs"
          h="auto"
          minH="0"
          px="0"
          flexShrink={0}
          fontSize="11.5px"
          fontWeight="normal"
          color="fg.muted"
          _hover={{ textDecoration: 'underline', color: 'fg.muted' }}
        >
          {t('invitations.modal.collab.wizard.done.copyLinkSecondary')}
        </Button>
      </SafeClipboardTrigger>
    </ClipboardRoot>
  );
};

/** One recipient row on the bulk done screen. A delivered channel leads with
 * its status and offers a discrete copy; link-only / failed keep the URL and
 * badge as the primary affordance. */
const ResultLine = ({ line }: { line: CollabSendResultLine }) => {
  const { t } = useTranslation();
  const delivered =
    line.status === 'sent' && !!line.lookupKey && isDeliveredStatus(line.deliveryStatus);

  return (
    <HStack
      gap="2.5"
      borderWidth="1px"
      borderColor="cardBorder"
      rounded="lg"
      bg="panelBg"
      px="12px"
      py="2"
    >
      <Box flex="1" minW="0">
        <Text
          fontFamily="heading"
          fontSize="12.5px"
          fontWeight="semibold"
          color="dark"
          lineClamp={1}
        >
          {recipientNameFor(line, t)}
        </Text>
        {line.status === 'sent' && line.lookupKey ? (
          delivered ? (
            <DeliveredStatusLine line={line} />
          ) : (
            <>
              <Text fontSize="11.5px" color="fg.muted" lineClamp={1}>
                {collabLinkUrl(line.lookupKey)}
              </Text>
              <DeliveryStatusBadge line={line} />
            </>
          )
        ) : (
          <HStack gap="1" color="fg.error" align="flex-start">
            <Box display="inline-flex" flexShrink={0} mt="0.5">
              <WarningTriangle width={11} height={11} />
            </Box>
            <Text fontSize="11.5px" lineClamp={2}>
              <Text as="span" fontWeight="semibold">
                {t('invitations.modal.collab.wizard.done.notSent')}
              </Text>{' '}
              <Text as="span">{failureMessage(line, t)}</Text>
            </Text>
          </HStack>
        )}
      </Box>
      {line.status === 'sent' &&
        line.lookupKey &&
        (delivered ? (
          // Reached the recipient: the link is a quiet secondary affordance.
          <SecondaryCopyLink lookupKey={line.lookupKey} />
        ) : (
          // Link-only / failed: the copy is the primary action for this line.
          <ClipboardCopyButton
            value={collabLinkUrl(line.lookupKey)}
            size="xs"
            variant="outline"
            label={t('invitations.modal.collab.wizard.done.copy')}
          />
        ))}
    </HStack>
  );
};

/**
 * Done screen of the sponsored wizard, shaped by the delivery mode picked on
 * the channel step. Email mono (delivered): "Proposal sent" with the recipient
 * confirmation and the personal link as a direct-share fallback. Link and DM:
 * "Proposal created" with the prominent copy field. Mono adds the expiry line
 * and a shortcut to the active conversation when one exists (cold DMs stay
 * impossible). Bulk: one line per recipient with its own copyable link; failed
 * lines carry their backend message instead. When nothing went out, the screen
 * says so: a failure title, the reason, no link and no expiry.
 */
const CollabDoneStep = ({ onClose }: CollabDoneStepProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const {
    collabSendResults,
    respondByDays,
    collabRecipientIdentityId,
    collabDeliveryMode,
    collabBriefApplicationId,
    collabType,
    creator,
  } = useInvitationData();
  const { data: contactSummary } = useContactSummaryQuery(
    user?.currentBrandId,
    collabRecipientIdentityId
  );

  const results = collabSendResults ?? [];
  const isBulk = results.length > 1;
  const mono = !isBulk ? results[0] : undefined;
  const sentCount = results.filter((line) => line.status === 'sent').length;
  // Nothing left: the screen says so, and promises neither a link nor a delay.
  const nothingSent = results.length > 0 && sentCount === 0;

  // "Proposal sent" only when the brand chose email AND the backend confirmed
  // the delivery; an email that fell back (or link/DM) keeps the "created"
  // framing where the link is the primary affordance.
  // A creator who applied is reached on her own account by the platform rail,
  // whatever the mode: the proposal was sent, and she is not an affiliate.
  const monoApplicantNotified =
    !!collabBriefApplicationId && mono?.deliveryStatus === 'notified_affiliate';
  const monoEmailSent =
    monoApplicantNotified ||
    (collabDeliveryMode === 'email' && !!mono && isDeliveredStatus(mono.deliveryStatus));

  const activeConversationId =
    !isBulk && creator ? (contactSummary?.activeIgConversationId ?? null) : null;

  const openConversation = () => {
    if (!activeConversationId) return;
    onClose();
    navigate(`/messages/${activeConversationId}`);
  };

  // A UGC proposal is followed on the UGC page, opened on the collab just sent;
  // a bulk send opens the page itself, where every collab it sent is listed.
  const trackedCollabId =
    collabType === 'ugc' && mono?.status === 'sent' ? (mono.collabId ?? null) : null;
  const trackingHref = trackedCollabId
    ? collabDashboardHref({ id: trackedCollabId, collabType: CollabType.UGC })
    : collabType === 'ugc' && isBulk && sentCount > 0
      ? UGC_TRACKING_PATH
      : null;
  const openTracking = () => {
    if (!trackingHref) return;
    onClose();
    navigate(trackingHref);
  };

  return (
    <Flex direction="column" gap="4" align="center" textAlign="center" pt="4">
      {nothingSent ? (
        <Center boxSize="48px" rounded="full" bg="red.subtle" color="red.fg">
          <WarningTriangle width={22} height={22} strokeWidth={2.2} />
        </Center>
      ) : (
        <Center boxSize="48px" rounded="full" bg="green.100" color="green.700">
          <Check width={22} height={22} strokeWidth={2.2} />
        </Center>
      )}
      <Box>
        <Text fontFamily="heading" fontSize="16px" fontWeight="semibold" color="dark">
          {nothingSent
            ? isBulk
              ? t('invitations.modal.collab.wizard.done.titleBulkFailed')
              : t('invitations.modal.collab.wizard.done.titleFailed')
            : isBulk
              ? t('invitations.modal.collab.wizard.done.titleBulk', { count: sentCount })
              : monoEmailSent
                ? t('invitations.modal.collab.wizard.done.titleEmailSent')
                : t('invitations.modal.collab.wizard.done.title')}
        </Text>
        <Text
          fontSize="12.5px"
          color={nothingSent && mono ? 'fg.error' : 'fg.muted'}
          maxW="400px"
          mt="1.5"
          lineHeight="1.5"
        >
          {nothingSent
            ? mono
              ? failureMessage(mono, t)
              : t('invitations.modal.collab.wizard.done.textBulkFailed')
            : isBulk
              ? t('invitations.modal.collab.wizard.done.textBulk')
              : monoApplicantNotified
                ? t('invitations.modal.collab.wizard.done.textApplicantNotified')
                : monoEmailSent
                  ? t('invitations.modal.collab.wizard.done.textEmailSent', {
                      name: recipientNameFor(mono, t),
                    })
                  : t('invitations.modal.collab.wizard.done.text', {
                      name: recipientNameFor(mono, t),
                    })}
        </Text>
      </Box>

      {mono &&
        mono.status === 'sent' &&
        mono.lookupKey &&
        (monoEmailSent ? (
          // Sent by email: the title carries the outcome, the personal link is
          // offered as a direct-share fallback below its lead-in.
          <VStack w="full" maxW="440px" gap="1.5">
            <Text fontSize="11.5px" color="fg.muted">
              {t('invitations.modal.collab.wizard.done.orShareLink')}
            </Text>
            <CopyLinkField
              value={collabLinkUrl(mono.lookupKey)}
              copyLabel={t('invitations.modal.collab.wizard.done.copy')}
            />
          </VStack>
        ) : (
          // Link / DM / not delivered: the link IS the delivery, so the full
          // URL card stays the primary action, with the outcome badge below.
          <VStack w="full" maxW="440px" gap="1.5">
            <CopyLinkField
              value={collabLinkUrl(mono.lookupKey)}
              copyLabel={t('invitations.modal.collab.wizard.done.copy')}
            />
            <DeliveryStatusBadge line={mono} />
          </VStack>
        ))}

      {isBulk && (
        <VStack align="stretch" gap="2" w="full" maxH="260px" overflowY="auto" textAlign="left">
          {results.map((line, index) => (
            <ResultLine key={`${line.displayName}-${index}`} line={line} />
          ))}
        </VStack>
      )}

      {!nothingSent && (
        <HStack gap="1.5" color="fg.muted">
          <Timer width={13} height={13} />
          <Text fontSize="11.5px">
            {t('invitations.modal.collab.wizard.done.expiry', { count: respondByDays })}
          </Text>
        </HStack>
      )}

      <Flex justify="center" gap="2" pt="1" pb="2">
        <Button variant="outline" onClick={onClose}>
          {t('common.close')}
        </Button>
        {trackingHref && (
          <Button variant="outline" onClick={openTracking}>
            <Eye width={15} height={15} />
            {t('invitations.modal.collab.wizard.done.viewTracking')}
          </Button>
        )}
        {activeConversationId && (
          <Button colorPalette="primary" onClick={openConversation}>
            <ChatBubble width={15} height={15} />
            {t('invitations.modal.collab.wizard.done.openConversation', {
              name: recipientNameFor(mono, t),
            })}
          </Button>
        )}
      </Flex>
    </Flex>
  );
};

export default CollabDoneStep;
